import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { UserGroupIcon, XMarkIcon, CheckIcon, UserIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import useRaciAssignments from '../../hooks/useRaciAssignments';
import { api } from '../../utils/requestInterceptor';

const RACI_COLORS = {
  R: 'bg-green-900/50 text-green-300 border-green-700',
  A: 'bg-blue-900/50 text-blue-300 border-blue-700',
  C: 'bg-purple-900/50 text-purple-300 border-purple-700',
  I: 'bg-yellow-900/50 text-yellow-300 border-yellow-700'
};

const RACI_TITLES = {
  R: 'Responsible - The people that are completing the work',
  A: 'Accountable - The people responsible for the overall project',
  C: 'Consulted - The people that need to be consulted for the project',
  I: 'Informed - The people that need to be kept informed'
};

const BulkRaciAssignment = ({ phases = [], projectId, onAssignmentsUpdated }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState('R');
  const [selectedEntities, setSelectedEntities] = useState({
    phases: [],
    steps: []
  });
  const [selectionMode, setSelectionMode] = useState('all'); // 'all', 'phases', 'steps'
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Use our custom RACI assignments hook
  const { 
    updateAssignment, 
    isLoading: raciLoading,
    getRaciUsers,
    refreshAssignments
  } = useRaciAssignments(projectId);
  
  // Fetch users when component mounts
  useEffect(() => {
    fetchUsers();
  }, []);
  
  // Fetch users when modal opens
  const openModal = async () => {
    setIsOpen(true);
    // Only refetch users if the list is empty
    if (users.length === 0) {
      await fetchUsers();
    }
  };
  
  const closeModal = () => {
    setIsOpen(false);
    resetForm();
  };
  
  const resetForm = () => {
    setSelectedRole('R');
    setSelectedEntities({ phases: [], steps: [] });
    setSelectionMode('all');
    setSelectedUsers([]);
  };
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/users');
      if (response.data && response.data.users) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle selection mode change
  const handleSelectionModeChange = (mode) => {
    setSelectionMode(mode);
    setSelectedEntities({ phases: [], steps: [] });
  };
  
  // Handle selecting all phases
  const handleSelectAllPhases = () => {
    if (selectionMode === 'phases' || selectionMode === 'all') {
      const allPhaseIds = phases.map(phase => phase.id);
      // If all phases are already selected, deselect all
      if (selectedEntities.phases.length === allPhaseIds.length) {
        setSelectedEntities({
          ...selectedEntities,
          phases: []
        });
      } else {
        setSelectedEntities({
          ...selectedEntities,
          phases: allPhaseIds
        });
      }
    }
  };
  
  // Handle selecting all steps
  const handleSelectAllSteps = () => {
    if (selectionMode === 'steps' || selectionMode === 'all') {
      let allStepIds = [];
      phases.forEach(phase => {
        if (phase.steps) {
          allStepIds = [...allStepIds, ...phase.steps.map(step => step.id)];
        }
      });
      
      // If all steps are already selected, deselect all
      if (selectedEntities.steps.length === allStepIds.length) {
        setSelectedEntities({
          ...selectedEntities,
          steps: []
        });
      } else {
        setSelectedEntities({
          ...selectedEntities,
          steps: allStepIds
        });
      }
    }
  };
  
  // Handle toggling a single phase
  const handleTogglePhase = (phaseId) => {
    if (selectionMode === 'steps') return;
    
    setSelectedEntities(prev => {
      const isSelected = prev.phases.includes(phaseId);
      if (isSelected) {
        return {
          ...prev,
          phases: prev.phases.filter(id => id !== phaseId)
        };
      } else {
        return {
          ...prev,
          phases: [...prev.phases, phaseId]
        };
      }
    });
  };
  
  // Handle toggling a single step
  const handleToggleStep = (stepId) => {
    if (selectionMode === 'phases') return;
    
    setSelectedEntities(prev => {
      const isSelected = prev.steps.includes(stepId);
      if (isSelected) {
        return {
          ...prev,
          steps: prev.steps.filter(id => id !== stepId)
        };
      } else {
        return {
          ...prev,
          steps: [...prev.steps, stepId]
        };
      }
    });
  };
  
  // Handle toggling a user selection
  const handleToggleUser = (userId) => {
    setSelectedUsers(prev => {
      const isSelected = prev.includes(userId);
      if (isSelected) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };
  
  // Apply the bulk assignments
  const handleApplyAssignments = async () => {
    // Validate that we have something to work with
    if (selectedEntities.phases.length === 0 && selectedEntities.steps.length === 0) {
      toast.error('Please select at least one phase or step');
      return;
    }
    
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    
    setLoading(true);
    
    try {
      // Calculate total number of assignments to make
      const totalAssignments = 
        (selectedEntities.phases.length * selectedUsers.length) + 
        (selectedEntities.steps.length * selectedUsers.length);
      
      let completedAssignments = 0;
      const toastId = toast.loading(`Applying assignments: 0/${totalAssignments}`);
      
      // Process phase assignments
      if (selectedEntities.phases.length > 0) {
        for (const phaseId of selectedEntities.phases) {
          for (const userId of selectedUsers) {
            await updateAssignment('phase', phaseId, selectedRole, userId, true);
            completedAssignments++;
            toast.loading(`Applying assignments: ${completedAssignments}/${totalAssignments}`, { id: toastId });
          }
        }
      }
      
      // Process step assignments
      if (selectedEntities.steps.length > 0) {
        for (const stepId of selectedEntities.steps) {
          for (const userId of selectedUsers) {
            await updateAssignment('step', stepId, selectedRole, userId, true);
            completedAssignments++;
            toast.loading(`Applying assignments: ${completedAssignments}/${totalAssignments}`, { id: toastId });
          }
        }
      }
      
      toast.success(`${completedAssignments} assignments applied successfully`, { id: toastId });
      
      // Call refreshAssignments function from the hook to refresh the local state
      await refreshAssignments();
      
      // Notify parent component that assignments were updated
      if (typeof onAssignmentsUpdated === 'function') {
        onAssignmentsUpdated();
      }
      
      // Use the global project refresh function if available
      if (typeof window.fetchProjectDetails === 'function') {
        window.fetchProjectDetails({ silent: true });
      }
      
      closeModal();
    } catch (error) {
      console.error('Error applying bulk assignments:', error);
      toast.error('Failed to apply assignments');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <>
      <button
        onClick={openModal}
        className="inline-flex items-center px-2 py-1 border border-transparent text-xs leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-indigo-500"
      >
        <UserGroupIcon className="h-3 w-3 mr-1" />
        Bulk Assign
      </button>
      
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-gray-800 p-6 text-left align-middle shadow-xl transition-all border border-gray-700">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-100 flex justify-between items-center"
                  >
                    <span>Bulk Assign RACI Roles</span>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-200"
                      onClick={closeModal}
                    >
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </Dialog.Title>
                  
                  <div className="mt-4 space-y-6">
                    {/* Role Selection */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-300 mb-2">1. Select RACI Role</h4>
                      <div className="flex space-x-2">
                        {Object.keys(RACI_COLORS).map(role => (
                          <button
                            key={role}
                            onClick={() => setSelectedRole(role)}
                            className={`px-4 py-2 rounded-md border ${
                              selectedRole === role 
                                ? `${RACI_COLORS[role]} border-2` 
                                : 'border-gray-600 text-gray-300'
                            }`}
                          >
                            <div className="flex items-center">
                              <span className="font-bold mr-2">{role}</span>
                              <span className="text-xs">{role === 'R' ? 'Responsible' : 
                                                        role === 'A' ? 'Accountable' : 
                                                        role === 'C' ? 'Consulted' : 'Informed'}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                      <p className="mt-1 text-xs text-gray-400">{RACI_TITLES[selectedRole]}</p>
                    </div>
                    
                    {/* Selection Type */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-300 mb-2">2. What do you want to assign?</h4>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleSelectionModeChange('all')}
                          className={`px-4 py-2 rounded-md border ${
                            selectionMode === 'all' 
                              ? 'bg-indigo-900 border-indigo-600 text-indigo-200 border-2' 
                              : 'border-gray-600 text-gray-300'
                          }`}
                        >
                          Both Phases & Steps
                        </button>
                        <button
                          onClick={() => handleSelectionModeChange('phases')}
                          className={`px-4 py-2 rounded-md border ${
                            selectionMode === 'phases' 
                              ? 'bg-indigo-900 border-indigo-600 text-indigo-200 border-2' 
                              : 'border-gray-600 text-gray-300'
                          }`}
                        >
                          Phases Only
                        </button>
                        <button
                          onClick={() => handleSelectionModeChange('steps')}
                          className={`px-4 py-2 rounded-md border ${
                            selectionMode === 'steps' 
                              ? 'bg-indigo-900 border-indigo-600 text-indigo-200 border-2' 
                              : 'border-gray-600 text-gray-300'
                          }`}
                        >
                          Steps Only
                        </button>
                      </div>
                    </div>
                    
                    {/* Entity Selection */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-300 mb-2">
                        3. Select Items 
                        <span className="ml-2 text-xs text-gray-400">
                          (Selected: {selectedEntities.phases.length} phases, {selectedEntities.steps.length} steps)
                        </span>
                      </h4>
                      
                      <div className="flex space-x-2 mb-2">
                        {(selectionMode === 'all' || selectionMode === 'phases') && (
                          <button
                            onClick={handleSelectAllPhases}
                            className="px-2 py-1 text-xs rounded-md border border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600"
                          >
                            {selectedEntities.phases.length === phases.length ? 'Deselect All Phases' : 'Select All Phases'}
                          </button>
                        )}
                        {(selectionMode === 'all' || selectionMode === 'steps') && (
                          <button
                            onClick={handleSelectAllSteps}
                            className="px-2 py-1 text-xs rounded-md border border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600"
                          >
                            {selectedEntities.steps.length === phases.flatMap(p => p.steps || []).length ? 'Deselect All Steps' : 'Select All Steps'}
                          </button>
                        )}
                      </div>
                      
                      <div className="max-h-60 overflow-y-auto border border-gray-600 rounded-md p-2 bg-gray-900">
                        {phases.map(phase => (
                          <div key={phase.id} className="mb-2">
                            {/* Phase row */}
                            {(selectionMode === 'all' || selectionMode === 'phases') && (
                              <div 
                                className={`flex items-center p-2 rounded-md ${
                                  selectedEntities.phases.includes(phase.id) ? 'bg-indigo-900' : 'hover:bg-gray-700'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedEntities.phases.includes(phase.id)}
                                  onChange={() => handleTogglePhase(phase.id)}
                                  className="h-4 w-4 text-indigo-600 rounded bg-gray-700 border-gray-500"
                                />
                                <span className="ml-2 font-medium text-gray-200">{phase.name}</span>
                              </div>
                            )}
                            
                            {/* Steps under this phase */}
                            {(selectionMode === 'all' || selectionMode === 'steps') && phase.steps?.map(step => (
                              <div 
                                key={step.id}
                                className={`flex items-center p-2 rounded-md ml-6 ${
                                  selectedEntities.steps.includes(step.id) ? 'bg-indigo-900' : 'hover:bg-gray-700'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedEntities.steps.includes(step.id)}
                                  onChange={() => handleToggleStep(step.id)}
                                  className="h-4 w-4 text-indigo-600 rounded bg-gray-700 border-gray-500"
                                />
                                <span className="ml-2 text-gray-300">{step.name}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* User Selection */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-300 mb-2">
                        4. Select Users to Assign 
                        <span className="ml-2 text-xs text-gray-400">
                          (Selected: {selectedUsers.length} users)
                        </span>
                      </h4>
                      
                      <div className="max-h-60 overflow-y-auto border border-gray-600 rounded-md p-2 bg-gray-900">
                        <div className="grid grid-cols-2 gap-2">
                          {users.map(user => (
                            <div 
                              key={user.id}
                              className={`flex items-center p-2 rounded-md ${
                                selectedUsers.includes(user.id) ? 'bg-indigo-900' : 'hover:bg-gray-700'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={() => handleToggleUser(user.id)}
                                className="h-4 w-4 text-indigo-600 rounded bg-gray-700 border-gray-500"
                              />
                              <div className="ml-3 flex items-center">
                                {user.profile_image ? (
                                  <img 
                                    src={user.profile_image} 
                                    alt={user.name} 
                                    className="h-6 w-6 rounded-full mr-2"
                                  />
                                ) : (
                                  <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                                )}
                                <span className="text-gray-200">{user.name}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
                      onClick={closeModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500 ${
                        loading ? 'opacity-75 cursor-not-allowed' : ''
                      }`}
                      onClick={handleApplyAssignments}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Applying...
                        </>
                      ) : (
                        <>
                          <CheckIcon className="h-4 w-4 mr-2" />
                          Apply Assignments
                        </>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default BulkRaciAssignment; 
